import { post, get } from "./fetch.core";
import messages, { TransMsg } from "../i18n/messages";
import type {
  WarehouseTransferNote,
  WarehouseTransferNoteListResponse,
  WarehouseTransferNoteItem,
} from "../types/warehouseTransferNote";

export type WarehouseTransferListScope = "outgoing" | "incoming";

export type WarehouseTransferListView = "incomingOnDelivery" | "outgoingFromRetailStock" | "outgoingDrafts";

export function parsePosApiError(err: unknown): { message: string; productId?: string } {
  if (!(err instanceof Error)) {
    return { message: String(err) };
  }
  const match = err.message.match(/\{[\s\S]*\}$/);
  if (!match) {
    return { message: err.message };
  }
  try {
    const j = JSON.parse(match[0]) as { message?: string; productId?: string };
    return { message: j.message ?? err.message, productId: j.productId };
  } catch {
    return { message: err.message };
  }
}

export async function createWarehouseTransferNote(body: {
  fromWarehouseId: string;
  toRetailShopId: string;
  toWarehouseId: string;
  items: Pick<WarehouseTransferNoteItem, "productId" | "quantity">[];
}): Promise<WarehouseTransferNote> {
  const res = await post<WarehouseTransferNote>("/pos/warehouseTransferNote/create", body);
  return res.data;
}

export async function updateWarehouseTransferNote(body: {
  id: string;
  fromWarehouseId?: string;
  toRetailShopId?: string;
  toWarehouseId?: string;
  items?: Pick<WarehouseTransferNoteItem, "productId" | "quantity">[];
}): Promise<WarehouseTransferNote> {
  const res = await post<WarehouseTransferNote>("/pos/warehouseTransferNote/update", body);
  return res.data;
}

export async function submitWarehouseTransferNote(id: string): Promise<WarehouseTransferNote> {
  const res = await post<WarehouseTransferNote>("/pos/warehouseTransferNote/submit", { id });
  return res.data;
}

export async function listWarehouseTransferNotes(params: {
  scope?: WarehouseTransferListScope;
  view?: WarehouseTransferListView;
  paging: { page: number; size: number };
  status?: string;
}): Promise<WarehouseTransferNoteListResponse> {
  const res = await post<WarehouseTransferNoteListResponse>("/pos/warehouseTransferNote/list", params);
  return res.data;
}

export async function getWarehouseTransferNote(id: string): Promise<WarehouseTransferNote> {
  const res = await get<WarehouseTransferNote>(`/pos/warehouseTransferNote/single/${id}`);
  return res.data;
}

export async function receiveWarehouseTransferNote(
  id: string,
  items?: { productId: string; quantityReceived: number }[]
): Promise<WarehouseTransferNote> {
  const res = await post<WarehouseTransferNote>("/pos/warehouseTransferNote/receive", {
    id,
    ...(items?.length ? { items } : {}),
  });
  return res.data;
}

export async function completeReturnWarehouseTransferNote(id: string): Promise<WarehouseTransferNote> {
  const res = await post<WarehouseTransferNote>("/pos/warehouseTransferNote/completeReturn", { id });
  return res.data;
}

export function isReturnToEshopTransfer(
  note: Pick<WarehouseTransferNote, "fromWarehouseId" | "toWarehouseId">,
  eshopWarehouseId: string | undefined,
  retailWarehouseIds: string[] | undefined
): boolean {
  if (!eshopWarehouseId) return false;
  if (transferNoteIdString(note.toWarehouseId) !== String(eshopWarehouseId)) return false;
  const fromWh = transferNoteIdString(note.fromWarehouseId);
  return (retailWarehouseIds ?? []).some((id) => String(id) === fromWh);
}

export function formatWarehouseTransferApiMessage(code: string, t: (m: TransMsg) => string): string {
  const msg = messages;
  const map: Record<string, TransMsg> = {
    transferNoteNotFound: msg.warehouseTransfer.errNotFound,
    transferNotEditable: msg.warehouseTransfer.errNotEditable,
    submitFailed: msg.warehouseTransfer.errSubmitFailed,
    insufficientSourceStock: msg.warehouseTransfer.errInsufficientStock,
    receiveFailed: msg.warehouseTransfer.errReceiveFailed,
    receiveQtyMustMatchShipped: msg.warehouseTransfer.errReceiveQtyMismatch,
    completeReturnFailed: msg.warehouseTransfer.errCompleteReturnFailed,
    fromWarehouseNotInRetail: msg.warehouseTransfer.errGeneric,
    invalidFromWarehouse: msg.warehouseTransfer.errGeneric,
    toRetailNotFound: msg.warehouseTransfer.errGeneric,
    toWarehouseNotInDestinationRetail: msg.warehouseTransfer.errGeneric,
    productNotFound: msg.product.noProductsFound,
  };
  const tr = map[code];
  return tr ? t(tr) : code;
}

/** Normalize id fields that may be returned as strings or objects from JSON */
export function transferNoteIdString(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return String(v);
}
